import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ApiError, getSessionClock, refreshSession, type SessionClock } from './authApi';

const WARN_MS = 2 * 60 * 1000;
const POLL_MS = 30 * 1000;

interface Props { onExpired: () => void }

/**
 * Polls the (non-sliding) session clock and warns 2 min before the session ends.
 * Activity from other /api calls slides the idle timer on the server, and the next poll sees it.
 * A deadline is kept in LOCAL time derived from server-reported remaining time (no clock-skew trouble).
 */
const SessionWatcher: React.FC<Props> = ({ onExpired }) => {
  const [deadline, setDeadline] = useState<number | null>(null);
  const [extendable, setExtendable] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const expiredRef = useRef(false);

  const apply = useCallback((c: SessionClock) => {
    const t = Date.now();
    setDeadline(t + (c.expiresAt - c.serverNow));
    setExtendable(c.extendable);
    // `now` must advance with every poll: otherwise `remaining` is computed against the mount time
    // and the 2-minute warning never shows for a session that had more than 2 min left at mount.
    setNow(t);
  }, []);

  const poll = useCallback(async () => {
    try { apply(await getSessionClock()); }
    catch (e) {
      if (e instanceof ApiError && e.status === 401 && !expiredRef.current) { expiredRef.current = true; onExpired(); }
    }
  }, [apply, onExpired]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_MS);
    const vis = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', vis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', vis); };
  }, [poll]);

  const remaining = deadline === null ? Infinity : deadline - now;
  const warning = remaining <= WARN_MS;

  useEffect(() => {
    if (!warning) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    setNow(Date.now());
    return () => clearInterval(id);
  }, [warning]);

  useEffect(() => { if (warning && remaining <= 0) poll(); }, [warning, remaining <= 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const stay = async () => {
    setBusy(true); setFailed(false);
    try { const c = await refreshSession(); apply(c); setNow(Date.now()); }
    catch (e) {
      if (e instanceof ApiError && e.status === 401) onExpired(); else setFailed(true);
    } finally { setBusy(false); }
  };

  if (!warning || remaining <= 0) return null;
  const s = Math.ceil(remaining / 1000);
  const label = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  // Portal to <body>: inside #root the warning sat under the fullscreen result dialog (Radix, z-9990)
  // and was aria-hidden while that dialog was open.
  return createPortal(
    <div role="alertdialog" aria-labelledby="sess-title" aria-describedby="sess-desc"
      className="glass-float auth-rise pointer-events-auto fixed inset-x-3 z-session mx-auto max-w-md rounded-2xl p-4 print:hidden"
      style={{ bottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
      <p id="sess-title" className="text-sm font-semibold text-txt-primary">Sesja wygaśnie za {label}</p>
      <p id="sess-desc" className="mt-1 text-sm text-txt-secondary">
        {extendable ? 'Z powodu bezczynności zostaniesz wylogowany. Dane na ekranie zostaną zachowane.' : 'Osiągnięto maksymalny czas sesji (12 godzin). Po wygaśnięciu zaloguj się ponownie — dane na ekranie zostaną zachowane.'}
      </p>
      {failed && <p role="alert" className="mt-2 text-sm text-[color:var(--error)]">Nie udało się odświeżyć sesji. Spróbuj ponownie.</p>}
      {extendable && (
        <button type="button" onClick={stay} disabled={busy} autoFocus
          className="mt-3 min-h-[44px] w-full rounded-xl bg-accent-strong px-4 text-sm font-semibold text-txt-onAccent disabled:opacity-60">
          {busy ? 'Odświeżam…' : 'Zostań zalogowany'}
        </button>
      )}
    </div>,
    document.body,
  );
};

export default SessionWatcher;
